import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlan } from '../contexts/PlanContext';
import { getBranding } from '../lib/api';
import { 
  Package, 
  Users, 
  FileText, 
  CreditCard, 
  Bell, 
  House,
  SignOut,
  List,
  X,
  Translate,
  Gear,
  ChartBar,
  ShieldCheck,
  Crown,
  Lock,
  Receipt,
  CurrencyCircleDollar
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { isPartnerRole } from '../lib/partners';
import { SupportWidget } from './SupportWidget';

export const Layout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, language, setLanguage, tx } = useLanguage();
  const { hasFeature, plan, plan_name, isAtClientLimit, clientUsagePercent, max_clients, current_clients, handleUpgrade } = usePlan();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState('');
  const isDemo = user?.is_demo || JSON.parse(localStorage.getItem('user') || '{}').is_demo;

  useEffect(() => {
    getBranding()
      .then(res => { if (res.data?.logo_url) setCompanyLogo(res.data.logo_url); })
      .catch(() => {});
  }, []);

  // Warehouse role lockdown — prevent copy/paste/save/print/right-click and
  // disable text selection so warehouse staff can't lift customer data.
  useEffect(() => {
    if (user?.role !== 'warehouse') return;
    const block = (e) => { e.preventDefault(); return false; };
    const blockKey = (e) => {
      // Block Ctrl/Cmd + C/X/S/P/A and PrintScreen
      const k = e.key?.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && ['c', 'x', 's', 'p', 'a'].includes(k)) {
        e.preventDefault();
        return false;
      }
    };
    document.addEventListener('copy', block);
    document.addEventListener('cut', block);
    document.addEventListener('contextmenu', block);
    document.addEventListener('keydown', blockKey);
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    return () => {
      document.removeEventListener('copy', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('keydown', blockKey);
      document.body.style.userSelect = prev;
      document.body.style.webkitUserSelect = prev;
    };
  }, [user?.role]);

  const isWarehouse = user?.role === 'warehouse';
  const isPartnerViewer = isPartnerRole(user?.role);

  const navItems = isWarehouse
    ? [
        { path: '/packages', label: t('packages'), icon: Package },
      ]
    : isPartnerViewer
    ? [
        { path: '/customers', label: t('customers'), icon: Users },
        { path: '/packages', label: t('packages'), icon: Package },
      ]
    : [
        { path: '/dashboard', label: t('dashboard'), icon: House },
        { path: '/packages', label: t('packages'), icon: Package },
        { path: '/customers', label: t('customers'), icon: Users },
        { path: '/invoices', label: t('invoices'), icon: FileText, feature: 'invoices' },
        { path: '/payments', label: t('payments'), icon: CreditCard, feature: 'payment_links' },
        { path: '/notifications', label: t('notifications'), icon: Bell },
        { path: '/settings', label: t('settings'), icon: Gear },
        { path: '/billing', label: tx('Billing', 'Facturación', 'Facturation'), icon: Receipt },
        { path: '/accounting', label: tx('Accounting', 'Contabilidad', 'Comptabilité'), icon: CurrencyCircleDollar },
        { path: '/reports', label: tx('Reports', 'Reportes', 'Rapports'), icon: ChartBar },
        { path: '/admin', label: 'Admin', icon: ShieldCheck },
      ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 ship-gradient text-white fixed h-full">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-3">
            {companyLogo ? (
              <div className="bg-white rounded-xl p-2 w-full flex items-center justify-center" data-testid="sidebar-company-logo">
                <img src={companyLogo} alt="Company Logo" className="h-14 max-w-[180px] object-contain" />
              </div>
            ) : (
              <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-20" />
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const isLocked = item.feature && !hasFeature(item.feature);
            return (
              <Link
                key={item.path}
                to={isLocked ? '#' : item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                className={`sidebar-link ${isActive ? 'active' : ''} ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={isLocked ? (e) => e.preventDefault() : undefined}
                title={isLocked ? `Upgrade to Pro to unlock ${item.label}` : ''}
              >
                <Icon size={20} weight={isActive ? 'fill' : 'duotone'} />
                <span className="font-medium text-sm">{item.label}</span>
                {isLocked && <Lock size={14} className="ml-auto text-white/40" />}
              </Link>
            );
          })}
        </nav>

        {/* Plan Badge */}
        {!isPartnerViewer && (
        <div className="mx-4 mb-2 p-3 bg-white/5 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-1.5">
            <Crown size={14} weight="fill" className="text-[#FF9900]" />
            <span className="text-xs font-semibold text-white uppercase tracking-wide">{plan_name} Plan</span>
          </div>
          <div className="text-[10px] text-white/50 mb-1.5">
            Clients: {current_clients}/{max_clients === -1 ? '∞' : max_clients}
          </div>
          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${clientUsagePercent >= 90 ? 'bg-red-400' : clientUsagePercent >= 70 ? 'bg-yellow-400' : 'bg-[#FF9900]'}`}
              style={{ width: `${Math.min(clientUsagePercent, 100)}%` }}
            />
          </div>
          {plan !== 'enterprise' && (
            <button onClick={handleUpgrade} className="block text-[10px] text-[#FF9900] hover:text-[#FFB060] mt-1.5 font-medium" data-testid="sidebar-upgrade-btn">
              Upgrade Plan
            </button>
          )}
        </div>
        )}

        {/* Language Toggle & User Section */}
        <div className="p-4 border-t border-white/10">
          {/* Language Flags */}
          <div className="flex items-center gap-1 px-2 py-2 mb-3" data-testid="language-toggle">
            {[
              { code: 'en', flag: '🇺🇸', label: 'EN' },
              { code: 'es', flag: '🇪🇸', label: 'ES' },
              { code: 'fr', flag: '🇫🇷', label: 'FR' },
            ].map(f => (
              <button
                key={f.code}
                onClick={() => setLanguage(f.code)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  language === f.code
                    ? 'bg-[#FF9900] text-white shadow-sm'
                    : 'text-white/50 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`sidebar-lang-${f.code}`}
              >
                <span className="text-sm">{f.flag}</span>
                <span>{f.label}</span>
              </button>
            ))}
          </div>

          {/* User Info */}
          <div className="flex items-center gap-3 px-4 py-2">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center overflow-hidden" data-testid="sidebar-user-avatar">
              {companyLogo ? (
                <img src={companyLogo} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-bold">{user?.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/60 truncate">{user?.email}</p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="sidebar-link w-full mt-2 text-white/60 hover:text-white"
          >
            <SignOut size={20} weight="duotone" />
            <span className="font-medium text-sm">{t('logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 ship-gradient text-white z-40 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          {companyLogo ? (
            <div className="bg-white rounded-lg p-1.5">
              <img src={companyLogo} alt="Company Logo" className="h-10 object-contain" />
            </div>
          ) : (
            <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-16" />
          )}
        </Link>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-white/10 rounded-lg p-0.5">
            {[
              { code: 'en', flag: '🇺🇸' },
              { code: 'es', flag: '🇪🇸' },
              { code: 'fr', flag: '🇫🇷' },
            ].map(f => (
              <button
                key={f.code}
                onClick={() => setLanguage(f.code)}
                className={`px-1.5 py-1 rounded text-sm transition-all ${language === f.code ? 'bg-[#FF9900]' : 'opacity-50 hover:opacity-100'}`}
              >
                {f.flag}
              </button>
            ))}
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-white/10 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 z-30 ship-gradient pt-16"
          >
            <nav className="py-4">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const isLocked = item.feature && !hasFeature(item.feature);
                return (
                  <Link
                    key={item.path}
                    to={isLocked ? '#' : item.path}
                    onClick={isLocked ? (e) => e.preventDefault() : () => setMobileMenuOpen(false)}
                    className={`sidebar-link ${isActive ? 'active' : ''} ${isLocked ? 'opacity-50' : ''}`}
                  >
                    <Icon size={20} weight={isActive ? 'fill' : 'duotone'} />
                    <span className="font-medium text-sm">{item.label}</span>
                    {isLocked && <Lock size={14} className="ml-auto text-white/40" />}
                  </Link>
                );
              })}
              <button
                onClick={handleLogout}
                className="sidebar-link w-full text-white/60 hover:text-white mt-4"
              >
                <SignOut size={20} weight="duotone" />
                <span className="font-medium text-sm">{t('logout')}</span>
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0">
        {/* Page Header */}
        <header className="bg-white border-b border-gray-200 px-8 py-5 sticky top-0 lg:top-0 z-20">
          <div className="flex items-center justify-between">
            <h1 
              className="text-3xl lg:text-4xl font-bold text-gray-900 tracking-wide premium-title"
              style={{ fontFamily: 'Bebas Neue' }}
              key={title}
            >
              {title}
            </h1>
          </div>
        </header>

        {/* Demo Banner */}
        {isDemo && (
          <div className="bg-[#FF9900]/10 border-b border-[#FF9900]/20 px-8 py-3 flex items-center justify-between" data-testid="demo-banner">
            <p className="text-sm text-[#FF9900] font-medium">
              {tx('Demo Mode — Read-only access. Data cannot be modified.', 'Modo Demo — Solo lectura. Los datos no pueden ser modificados.', 'Mode Demo — Acces en lecture seule.')}
            </p>
            <a href="https://sqawk.app/book/MrPositivity" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-[#FF9900] hover:underline">
              {tx('Get Your Own Account', 'Obtener Tu Cuenta', 'Obtenir Votre Compte')} &rarr;
            </a>
          </div>
        )}

        {/* Page Content */}
        <div className="p-5 md:p-8">
          {children}
        </div>
      </main>
      <SupportWidget />
    </div>
  );
};
